import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Nav from "@/components/Nav";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, XCircle, Pencil, Rocket, Flag } from "lucide-react";

const DIFF_LABELS = {
  1: "Foundational",
  2: "Intermediate",
  3: "Advanced",
  4: "Expert",
  5: "Exceptional",
};

const STATUS_COLORS = {
  draft: "text-slate-300 bg-slate-700/30 border-slate-600",
  needs_revision: "text-amber-200 bg-amber-500/10 border-amber-500/40",
  approved_for_pilot: "text-emerald-200 bg-emerald-500/10 border-emerald-500/40",
  under_calibration: "text-sky-200 bg-sky-500/10 border-sky-500/40",
  calibrated: "text-[#F0C28B] bg-[#E0A96D]/10 border-[#E0A96D]/40",
  retired: "text-rose-200 bg-rose-500/10 border-rose-500/40",
};

export default function AdminDrafts() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDomain, setFilterDomain] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterDiff, setFilterDiff] = useState("");
  const [selected, setSelected] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = await api.get("/admin/drafts/stats");
      setStats(s.data);
      const params = {};
      if (filterDomain) params.domain = filterDomain;
      if (filterStatus) params.status = filterStatus;
      if (filterDiff) params.difficulty = filterDiff;
      const { data } = await api.get("/admin/drafts", { params });
      setItems(data.items);
      setSelected(new Set());
    } catch (e) {
      if (e?.response?.status === 401) nav("/auth");
      else toast.error("Failed to load drafts");
    } finally {
      setLoading(false);
    }
  }, [nav, filterDomain, filterStatus, filterDiff]);

  useEffect(() => { load(); }, [load]);

  const toggleOne = (qid) => {
    setSelected(prev => {
      const n = new Set(prev);
      n.has(qid) ? n.delete(qid) : n.add(qid);
      return n;
    });
  };

  const toggleAll = () => {
    setSelected(prev => prev.size === items.length ? new Set() : new Set(items.map(i => i.question_id)));
  };

  const setStatus = async (qid, status) => {
    try {
      await api.post(`/admin/drafts/${qid}/status`, { status });
      toast.success(`Marked ${qid} as ${status.replace(/_/g, " ")}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const bulkStatus = async (status) => {
    if (selected.size === 0) return toast.info("Select items first");
    try {
      const { data } = await api.post("/admin/drafts/bulk-status", {
        question_ids: Array.from(selected), status,
      });
      toast.success(`${data.updated} items → ${status.replace(/_/g, " ")}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk update failed"); }
  };

  const promote = async (qid) => {
    try {
      await api.post(`/admin/drafts/${qid}/promote`);
      toast.success(`${qid} promoted to live bank (inactive until stats accumulate)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Promote failed"); }
  };

  const domains = useMemo(() => Object.keys(stats?.by_domain || {}), [stats]);
  const statuses = stats?.known_statuses || [];

  return (
    <div><Nav />
      <div className="max-w-6xl mx-auto px-5 py-10">
        <button onClick={() => nav("/admin")} className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200 mb-4" data-testid="back-to-admin">
          <ArrowLeft className="w-4 h-4" /> Back to Admin
        </button>
        <div className="uppercase font-mono text-xs tracking-[0.24em] text-[#E0A96D] mb-2">Bank expansion</div>
        <h1 className="font-serif text-3xl sm:text-4xl mb-2">Question drafts</h1>
        <p className="text-slate-400 text-sm max-w-2xl mb-6">
          Candidate items from Batch 1. Nothing goes live until you promote an item.
          Promoted items enter the bank as <em>inactive</em>, pending pilot response data.
        </p>

        {/* Stats panel */}
        {stats && (
          <div className="rounded-2xl bg-[#131E36] border border-slate-800/70 p-5 mb-6">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
              <div><span className="text-slate-500">Total drafts:</span> <span className="font-mono text-slate-100">{stats.total}</span></div>
              {Object.entries(stats.by_status || {}).map(([s, n]) => (
                <div key={s}><span className="text-slate-500">{s.replace(/_/g, " ")}:</span> <span className="font-mono text-slate-100">{n}</span></div>
              ))}
            </div>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {Object.entries(stats.by_domain || {}).map(([d, byStatus]) => (
                <div key={d} className="text-xs text-slate-400">
                  <span className="font-mono text-slate-200">{d}:</span>{" "}
                  {Object.entries(byStatus).map(([s, n]) => `${s.replace(/_/g, " ")} ${n}`).join(" · ")}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-4">
          <select value={filterDomain} onChange={e => setFilterDomain(e.target.value)} className="bg-[#131E36] border border-slate-700 text-slate-200 text-sm rounded-lg px-3 py-2" data-testid="filter-domain">
            <option value="">All domains</option>
            {domains.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="bg-[#131E36] border border-slate-700 text-slate-200 text-sm rounded-lg px-3 py-2" data-testid="filter-status">
            <option value="">All statuses</option>
            {statuses.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
          <select value={filterDiff} onChange={e => setFilterDiff(e.target.value)} className="bg-[#131E36] border border-slate-700 text-slate-200 text-sm rounded-lg px-3 py-2" data-testid="filter-diff">
            <option value="">All difficulties</option>
            {[1,2,3,4,5].map(d => <option key={d} value={d}>{DIFF_LABELS[d]}</option>)}
          </select>
        </div>

        {/* Bulk bar */}
        <div className="flex flex-wrap items-center gap-2 mb-3 text-sm">
          <button onClick={toggleAll} className="px-3 py-1.5 rounded-lg border border-slate-700 hover:border-slate-500 text-slate-300" data-testid="toggle-all">
            {selected.size === items.length && items.length > 0 ? "Deselect all" : "Select all"}
          </button>
          <span className="text-slate-500">{selected.size} selected</span>
          <div className="ml-auto flex flex-wrap gap-2">
            <button onClick={() => bulkStatus("approved_for_pilot")} className="px-3 py-1.5 rounded-lg bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25 border border-emerald-500/40" data-testid="bulk-approve">
              <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" /> Approve
            </button>
            <button onClick={() => bulkStatus("needs_revision")} className="px-3 py-1.5 rounded-lg bg-amber-500/15 text-amber-200 hover:bg-amber-500/25 border border-amber-500/40" data-testid="bulk-revise">
              <Pencil className="w-3.5 h-3.5 inline mr-1" /> Needs revision
            </button>
            <button onClick={() => bulkStatus("retired")} className="px-3 py-1.5 rounded-lg bg-rose-500/15 text-rose-200 hover:bg-rose-500/25 border border-rose-500/40" data-testid="bulk-retire">
              <XCircle className="w-3.5 h-3.5 inline mr-1" /> Reject
            </button>
          </div>
        </div>

        {/* List */}
        {loading ? <div className="text-slate-500">Loading drafts…</div> : (
          <div className="space-y-3" data-testid="drafts-list">
            {items.length === 0 && <div className="text-slate-500">No drafts match the current filters.</div>}
            {items.map(it => (
              <div key={it.question_id} className="rounded-2xl bg-[#131E36] border border-slate-800/70 hover:border-slate-700 p-4" data-testid={`draft-${it.question_id}`}>
                <div className="flex items-start gap-3">
                  <input type="checkbox" checked={selected.has(it.question_id)} onChange={() => toggleOne(it.question_id)} className="mt-1" data-testid={`select-${it.question_id}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 text-xs font-mono text-slate-400 mb-2">
                      <span className="text-[#E0A96D]">{it.question_id}</span>
                      <span>· {it.domain}</span>
                      <span>· {DIFF_LABELS[it.provisional_difficulty]}</span>
                      <span>· {it.sub_skill}</span>
                      <span className={`px-2 py-0.5 rounded border ${STATUS_COLORS[it.status] || ""}`}>{it.status.replace(/_/g, " ")}</span>
                      {it.needs_review_flag && <span className="px-2 py-0.5 rounded bg-amber-500/15 text-amber-200 border border-amber-500/40"><Flag className="w-3 h-3 inline" /> flagged</span>}
                    </div>
                    <div className="font-serif text-lg mb-2 text-slate-100">{it.question_text}</div>
                    <ol className="list-decimal pl-5 text-sm text-slate-300 space-y-1 mb-2">
                      {it.answer_options.map((o, i) => (
                        <li key={`${it.question_id}-opt-${i}`} className={i === it.correct_answer ? "text-emerald-300" : ""}>
                          {o} {i === it.correct_answer && <span className="text-emerald-500 text-xs">✓ correct</span>}
                        </li>
                      ))}
                    </ol>
                    {it.explanation && <div className="text-xs text-slate-500 italic">Rationale: {it.explanation}</div>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3 text-xs">
                  <button onClick={() => setStatus(it.question_id, "approved_for_pilot")} className="px-2.5 py-1 rounded-lg bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25 border border-emerald-500/40" data-testid={`approve-${it.question_id}`}>Approve</button>
                  <button onClick={() => setStatus(it.question_id, "needs_revision")} className="px-2.5 py-1 rounded-lg bg-amber-500/15 text-amber-200 hover:bg-amber-500/25 border border-amber-500/40" data-testid={`revise-${it.question_id}`}>Needs revision</button>
                  <button onClick={() => setStatus(it.question_id, "retired")} className="px-2.5 py-1 rounded-lg bg-rose-500/15 text-rose-200 hover:bg-rose-500/25 border border-rose-500/40" data-testid={`retire-${it.question_id}`}>Reject</button>
                  {(it.status === "approved_for_pilot" || it.status === "calibrated") && (
                    <button onClick={() => promote(it.question_id)} className="ml-auto px-2.5 py-1 rounded-lg bg-[#E0A96D] text-[#070C18] hover:bg-[#F0C28B] font-semibold" data-testid={`promote-${it.question_id}`}>
                      <Rocket className="w-3 h-3 inline mr-1" /> Promote to live bank
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
